"use client"

/**
 * Shell-level question inspector — same rail stack as library secondary nav
 * (`ResizablePanel` + `ResizableHandle` + `NestedSecondaryPanelShell`).
 *
 * Body fields portal from {@link QuestionComposerProvider} into the shell host
 * so form context survives the DOM move.
 */

import * as React from "react"
import { createPortal } from "react-dom"

import { Form } from "@/components/ui/form"
import {
  NestedSecondaryPanelShell,
  SECONDARY_PANEL_WIDTH_DEFAULT,
  SECONDARY_PANEL_WIDTH_MAX,
  SECONDARY_PANEL_WIDTH_MIN,
  clampSecondaryPanelWidth,
} from "@/components/templates/nested-secondary-panel-shell"
import {
  ResizableHandle,
  ResizablePanel,
} from "@/components/ui/resizable"
import { NAV_FLYOUT_SCROLL_BODY } from "@exxatdesignux/ui/lib/nav-flyout-inset"
import { useShellRailWidth } from "@exxatdesignux/ui/hooks/use-shell-rail-width"
import { useSidebar } from "@/components/ui/sidebar"
import { useIsMobile } from "@/hooks/use-mobile"
import { useSidebarReflowZoom } from "@/hooks/use-sidebar-reflow-zoom"
import { useAskLeoDockedPanelOpen } from "@/hooks/use-ask-leo-docked-panel-open"
import { SecondaryPanelFlyoutHeader } from "@/components/sidebar"
import { SecondaryHubNavCompactShell } from "@/components/sidebar"

import {
  useQuestionComposerContext,
  useQuestionComposerInspectorSurface,
} from "./composer-context"
import { QuestionComposerInspectorFields, QuestionInspectorCompactNav } from "./composer-inspector"

export const QUESTION_INSPECTOR_WIDTH_KEY = "shell:question-inspector-width"

type QuestionInspectorShellBridgeState = {
  inspectorOpen: boolean
  setInspectorOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const QuestionInspectorShellBridgeContext =
  React.createContext<{
    bridge: QuestionInspectorShellBridgeState | null
    setBridge: (value: QuestionInspectorShellBridgeState | null) => void
    bodyHost: HTMLElement | null
    setBodyHost: (element: HTMLElement | null) => void
  } | null>(null)

export function QuestionInspectorShellBridgeProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [bridge, setBridge] =
    React.useState<QuestionInspectorShellBridgeState | null>(null)
  const [bodyHost, setBodyHost] = React.useState<HTMLElement | null>(null)
  const value = React.useMemo(
    () => ({ bridge, setBridge, bodyHost, setBodyHost }),
    [bridge, bodyHost],
  )
  return (
    <QuestionInspectorShellBridgeContext.Provider value={value}>
      {children}
    </QuestionInspectorShellBridgeContext.Provider>
  )
}

function useQuestionInspectorShellBridge() {
  const ctx = React.useContext(QuestionInspectorShellBridgeContext)
  if (!ctx) {
    throw new Error(
      "Question inspector shell requires QuestionInspectorShellBridgeProvider in the app layout.",
    )
  }
  return ctx
}

function QuestionInspectorPanelBody({
  inspectorOpen,
  setInspectorOpen,
}: {
  inspectorOpen: boolean
  setInspectorOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { inspectorPanel, openProperties } = useQuestionComposerContext()
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const showCompactRail = !inspectorOpen && !navFlyout

  if (showCompactRail) {
    return (
      <SecondaryHubNavCompactShell
        ariaLabel="Question inspector"
        side="right"
        onExpand={openProperties}
      >
        <QuestionInspectorCompactNav />
      </SecondaryHubNavCompactShell>
    )
  }

  return (
    <>
      <SecondaryPanelFlyoutHeader
        title={inspectorPanel === "leo" ? "Leo spotted" : "Properties"}
        flyout={navFlyout}
        side="right"
        onCollapse={() => setInspectorOpen(false)}
        onCloseFlyout={() => setInspectorOpen(false)}
      />
      <div className={NAV_FLYOUT_SCROLL_BODY}>
        <div className="flex flex-col gap-4 px-3 py-3">
          <QuestionComposerInspectorFields />
        </div>
      </div>
    </>
  )
}

/**
 * Registers open state and portals inspector fields into the shell body host.
 * Render inside {@link QuestionComposerProvider}.
 */
export function QuestionComposerInspectorShellBridge() {
  const { form, inspectorOpen, setInspectorOpen } = useQuestionComposerContext()
  const inspectorSurface = useQuestionComposerInspectorSurface()
  const { setBridge, bodyHost } = useQuestionInspectorShellBridge()

  React.useEffect(() => {
    if (inspectorSurface !== "secondary-panel") {
      setBridge(null)
      return
    }
    setBridge({ inspectorOpen, setInspectorOpen })
    return () => setBridge(null)
  }, [inspectorOpen, inspectorSurface, setBridge, setInspectorOpen])

  if (inspectorSurface !== "secondary-panel" || !bodyHost) {
    return null
  }

  return createPortal(
    <Form {...form}>
      <QuestionInspectorPanelBody
        inspectorOpen={inspectorOpen}
        setInspectorOpen={setInspectorOpen}
      />
    </Form>,
    bodyHost,
  )
}

/**
 * Docked inspector rail — `ResizableHandle` + `ResizablePanel` in the workspace
 * group. Mount once in `App.tsx` beside {@link AskLeoDockedSlot}.
 */
export function QuestionComposerInspectorDockedSlot() {
  const { bridge, setBodyHost } = useQuestionInspectorShellBridge()
  const askLeoDockedOpen = useAskLeoDockedPanelOpen()
  const { isMobile } = useSidebar()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const rail = useShellRailWidth({
    storageKey: QUESTION_INSPECTOR_WIDTH_KEY,
    defaultWidth: SECONDARY_PANEL_WIDTH_DEFAULT,
    minWidth: SECONDARY_PANEL_WIDTH_MIN,
    maxWidth: SECONDARY_PANEL_WIDTH_MAX,
    clamp: clampSecondaryPanelWidth,
    version: 3,
  })

  React.useEffect(() => {
    return () => setBodyHost(null)
  }, [setBodyHost])

  if (!bridge || askLeoDockedOpen) {
    return null
  }

  const { inspectorOpen, setInspectorOpen } = bridge
  const overlay: false | "mobile" | "desktop" = navFlyout
    ? isMobile
      ? "mobile"
      : "desktop"
    : false
  const shellOpen = navFlyout ? inspectorOpen : true
  const compact = !navFlyout && !inspectorOpen
  const dockedResizable = shellOpen && !compact && overlay === false

  const bodyHost = (
    <div
      ref={setBodyHost}
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-question-inspector-body-host=""
    />
  )

  const shell = (
    <NestedSecondaryPanelShell
      open={shellOpen}
      compact={compact}
      overlay={overlay}
      side="right"
      dataSlot="question-inspector-panel"
      widthPersistKey={QUESTION_INSPECTOR_WIDTH_KEY}
      widthDefault={SECONDARY_PANEL_WIDTH_DEFAULT}
      ownSize={!dockedResizable && !(shellOpen && compact)}
      aria-label="Question inspector"
      className="h-full min-h-0 self-stretch"
      contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      {bodyHost}
    </NestedSecondaryPanelShell>
  )

  if (overlay !== false) {
    if (!shellOpen) return null
    return (
      <>
        <button
          type="button"
          aria-label="Close question inspector"
          className="fixed inset-0 z-[60] cursor-default bg-background/60"
          onClick={() => setInspectorOpen(false)}
        />
        {shell}
      </>
    )
  }

  if (!shellOpen) return null

  if (compact) {
    return (
      <>
        <ResizableHandle withHandle />
        <ResizablePanel
          id="shell-question-inspector-compact"
          defaultSize="3rem"
          minSize="3rem"
          maxSize="3rem"
          groupResizeBehavior="preserve-pixel-size"
          collapsible={false}
          className="min-h-0 min-w-0"
        >
          <NestedSecondaryPanelShell
            open
            compact
            overlay={false}
            side="right"
            ownSize={false}
            aria-label="Question inspector"
            contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            {bodyHost}
          </NestedSecondaryPanelShell>
        </ResizablePanel>
      </>
    )
  }

  return (
    <>
      <ResizableHandle withHandle />
      <ResizablePanel
        id="shell-question-inspector"
        {...rail.panelProps}
        className="min-h-0 min-w-0"
      >
        {shell}
      </ResizablePanel>
    </>
  )
}

/** @deprecated Use {@link QuestionComposerInspectorShellBridge}. */
export const QuestionComposerInspectorShellHost = QuestionComposerInspectorShellBridge
